import React, { useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import { VIEW_AS_CHANGED_EVENT, liveViewAs, viewAs } from '../services/viewAsState';

/**
 * The bar an admin sees while viewing as a member (Backend/admin_view_as.py):
 * whose account, read-only or with write access, the time left, and End. When
 * the view ends (End, its time runs out, or an admin ends it from User 360)
 * the page is covered: the tab never falls back to the admin's own account.
 */
const ViewAsBanner: React.FC = () => {
  const [, tick] = useState(0);
  useEffect(() => {
    const on = () => tick(n => n + 1);
    window.addEventListener(VIEW_AS_CHANGED_EVENT, on);
    const t = window.setInterval(on, 20_000);
    return () => { window.removeEventListener(VIEW_AS_CHANGED_EVENT, on); window.clearInterval(t); };
  }, []);
  const v = viewAs();
  if (!v) return null;
  const live = liveViewAs();
  const who = v.name ? `${v.name} (${v.email})` : v.email;
  if (!live) {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-white/95 px-6 font-space-grotesk">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold text-grayscale-1300">View as member has ended</h1>
          <p className="mt-2 text-sm text-grayscale-1100">
            You were viewing as {who}. Close this tab; start again from Admin → User 360 if you need to.
          </p>
          <button type="button" onClick={() => window.close()}
            className="mt-4 rounded-lg bg-primary-700 px-4 py-2 text-sm font-medium text-white hover:bg-primary-800">
            Close tab
          </button>
        </div>
      </div>
    );
  }
  const mins = Math.max(0, Math.ceil((Date.parse(live.expires_at) - Date.now()) / 60_000));
  const write = live.mode === 'write';
  return (
    <div role="status"
      className={`fixed bottom-3 left-1/2 z-[10000] -translate-x-1/2 flex max-w-[calc(100vw-24px)] items-center gap-3
        rounded-full px-4 py-2 text-xs shadow-lg font-space-grotesk text-white
        ${write ? 'bg-red-700' : 'bg-grayscale-1300'}`}>
      <span className="truncate">
        Viewing as <b>{who}</b> · {write ? 'write access — changes are saved' : 'read-only'} · {mins} min left
      </span>
      <button type="button" onClick={() => { void apiService.endViewAs(); }}
        className="shrink-0 rounded-full bg-white/20 px-3 py-1 font-medium hover:bg-white/30">
        End
      </button>
    </div>
  );
};

/** In place of the app when the tab's link did not work: never the admin's
 *  own account in a tab that was meant to show a member's. */
export const ViewAsFailed: React.FC<{ message: string }> = ({ message }) => (
  <div className="min-h-screen flex items-center justify-center bg-[#FCFBF9] px-6 font-space-grotesk">
    <div className="max-w-md text-center">
      <h1 className="text-xl font-semibold text-grayscale-1300">View as member did not start</h1>
      <p className="mt-2 text-sm text-grayscale-1100">{message}</p>
    </div>
  </div>
);

export default ViewAsBanner;
